import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAutoOffset, selectHudDimensions, setSpecificCenterOffset } from '../mapSlice';

export const useAutoMapOffset = () => {
    const dispatch = useDispatch();
    const isAutoOffset = useSelector(selectIsAutoOffset);
    const hudDimensions = useSelector(selectHudDimensions);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!isAutoOffset) return;

        const { width, height } = windowSize;
        const isLandscapeMobile = height <= 500 && width > height;

        // Calculate Default Offset (No HUD)
        // Shift down ~25% to show road ahead
        const defaultOffsetY = Math.round(height * 0.25);

        dispatch(setSpecificCenterOffset({
            mode: 'default',
            offset: { x: 0, y: defaultOffsetY }
        }));

        // Calculate HUD Offset (HUD Visible)
        let hudOffsetX = 0;
        let hudOffsetY = 0;

        if (isLandscapeMobile) {
            // HUD is on the left
            // Shift map center to the right
            hudOffsetX = Math.round(width * 0.25);
            // Slightly down for road ahead
            hudOffsetY = Math.round(height * 0.15);
        } else {
            // Portrait / Regular
            // HUD is at the bottom
            // Shift map center UP to clear HUD
            // Using HUD height + some buffer, OR percentage
            const hudHeight = hudDimensions?.height || 100; // fallback
            // We want the vehicle marker (center) to be above the HUD
            // Center is at 0,0 (mid screen). HUD top is at (ScreenBottom - Bottom - Height).
            // ScreenBottom is +H/2.
            // HUD Top Y coordinate relative to center = (H/2) - Bottom - Height.
            // But Leaflet offset: +Y moves map Center UP? No.
            // Center Offset: "Offset of the center point".
            // If offset is (0, 100), the map center (latlng) is projected to pixel point, then (0, 100) is subtracted?
            // See MapView.js: targetPoint = centerPoint.subtract([offsetX, offsetY]);
            // If offsetY is positive, targetPoint.y is LESS (Up).
            // So if we want the map center (vehicle) to be visually LOWER on screen, we need targetPoint.y to be HIGHER (Down).
            // Wait.
            // MapView: map.setView(targetLatLng).
            // If we want the Vehicle (LatLng) to be at Y=Start+100 (Lower on screen).
            // Then the Center of the Map (which is what setView sets) must be ABOVE the Vehicle.
            // So we need to setView to a LatLng that is North of the Vehicle.
            // `centerOffset`:
            // `targetPoint = centerPoint.subtract([offsetX, offsetY])`
            // If offsetY = 100. targetPoint.y = center.y - 100 (Up).
            // We tell map "This upper point is the center".
            // So the original center (vehicle) moves DOWN.
            // Correct:
            // +Y Offset -> Vehicle moves DOWN.
            // -Y Offset -> Vehicle moves UP.

            // So for DEFAULT (Show road ahead): We want Vehicle DOWN. So +Y.
            // defaultOffsetY = height * 0.25 (Positive). Correct.

            // For HUD (Portrait): We want Vehicle UP (to avoid HUD). So -Y.
            // How much?
            // We want to move it up enough so it's not covered.
            // HUD is at bottom.
            hudOffsetY = -Math.round(height * 0.20);
            // Or use actual HUD height logic:
            // hudOffsetY = -Math.round(hudDimensions.height * 0.8);
        }

        dispatch(setSpecificCenterOffset({
            mode: 'hud',
            offset: { x: hudOffsetX, y: hudOffsetY }
        }));

    }, [isAutoOffset, hudDimensions, windowSize, dispatch]);
};
